import * as React from "react";

export type PollingStopConditionType<TResult> = (context: {
	data: TResult | undefined;
	numberOfTries: number;
	startTime: Date;
}) => boolean;

interface PollingOptions<TResult> {
	/**
	 *  Callback for checkout stop condition after you got a result from the polling, runs after `effect`, the poller will stop scheduling next poll if the callback returns true.
	 */
	stopCondition: PollingStopConditionType<TResult>;
	/**
	 * Callback for handling API response, runs before stopCondition
	 */
	effect: (data: TResult | undefined) => void;
	/**
	 * (Optional) Cleanup function run when either `stopPolling` is called or the stop condition fulfilled.
	 * The running order is `effect` -> `stopCondition` -> (if stop condition reached) onStopped.
	 */
	onStopped?: () => void;
	/**
	 * (Optional) The timeout that will be passed to `setInterval`, default to 1000.
	 */
	interval?: number;
}

interface UsePollingProps<TResult> {
	poll: () => Promise<TResult>;
	options: PollingOptions<TResult>;
}

type UsePollingReturn = {
	startPolling: () => void;
	stopPolling: () => void;
	isPolling: boolean;
};

export const usePolling = <TResult>({ poll, options }: UsePollingProps<TResult>): UsePollingReturn => {
	const internalIsPollingRef = React.useRef(false);
	const [isPolling, setIsPollingState] = React.useState(false);
	const optionsRef = React.useRef(options);
	optionsRef.current = options;

	/**
	 * We need to shadow the isPolling value between component state and ref here
	 * because the state one is mainly for rerendering component tree,
	 * while the ref one is for catching race conditions.
	 */
	const setIsPolling = (state: boolean) => {
		internalIsPollingRef.current = state;
		setIsPollingState(state);
	};

	const timeoutRef = React.useRef<number | undefined>(undefined);
	const isAbortedRef = React.useRef(false);
	const triesRef = React.useRef(0);
	const startTimeRef = React.useRef<Date | undefined>(undefined);
	const interval = optionsRef.current.interval ?? 1000;

	const reset = React.useCallback(() => {
		setIsPolling(false);
		clearTimeout(timeoutRef.current);
		timeoutRef.current = undefined;
		isAbortedRef.current = false;
		triesRef.current = 0;
		startTimeRef.current = undefined;
	}, []);

	const stopPolling = React.useCallback(async () => {
		setIsPolling(false);

		optionsRef.current.onStopped?.();

		clearTimeout(timeoutRef.current);
		timeoutRef.current = undefined;
		isAbortedRef.current = true;
	}, []);

	// eslint-disable-next-line no-var -- To allow executePoll to call itself recursively and not falsly trigger react-hooks/immutability lint rule.
	var executePoll = React.useCallback(() => {
		const handleEffect = async (promise: Promise<TResult | undefined>) => {
			triesRef.current++;
			const data = await promise;

			if (!isAbortedRef.current) {
				optionsRef.current.effect(data);
			}

			const currentStopCondition = optionsRef.current.stopCondition;
			if (
				!currentStopCondition({
					data: data,
					numberOfTries: triesRef.current,
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					startTime: startTimeRef.current!,
				}) &&
				!isAbortedRef.current
			) {
				const handler: TimerHandler = () => {
					executePoll();
				};
				timeoutRef.current = setTimeout(handler, interval);
				return;
			}

			// This ref check is for handling the race condition where the callback of API call runs after stop
			if (internalIsPollingRef.current) {
				optionsRef.current.onStopped?.();
			}
			reset();
		};

		handleEffect(poll());
	}, [poll, interval, reset]);

	const startPolling = React.useCallback(() => {
		reset();

		startTimeRef.current = new Date();
		setIsPolling(true);
		executePoll();
	}, [executePoll, reset]);

	return { startPolling, stopPolling, isPolling };
};
